// Shared rundown derivation — the single source of truth for schedule in-times,
// on-air / next selection, and per-cue display status. Kept framework-free so
// both the React hook (useRundownRows) and non-render callers (RundownEditor's
// auto-mode interval, exports) can use the exact same maths. Previously this
// logic was duplicated inline across RundownTable, RundownEditor and Header;
// the redesign adds a second consumer (the modern spine) so it now lives once.

// Resolve a show's start clock to a concrete Date on TODAY. The stored
// wall-clock time-of-day is read via UTC getters so it's timezone-independent —
// see lib/showTime.js. Re-exported here for existing importers.
export { parseShowStartDate } from './showTime.js';
import { parseShowStartDate } from './showTime.js';

// What a cue contributes to the running order.
//
// A GROUP contributes NOTHING of its own. Its children follow it in the list
// and their durations ARE its duration — which is exactly what the rundown
// shows in a group's duration cell, where the value is read-only and labelled
// "Sum of child durations". A group's own planned_duration is dead data.
//
// Counting both made a 1-minute group occupy two minutes of running order: the
// group's minute, then its children's minute again, pushing every later cue and
// every later group back by the same amount and compounding down the show.
export function effectiveDurationSec(cue) {
  if (cue.cue_type === 'GROUP') return 0;
  return cue.planned_duration || 0;
}

// Total planned running time over a set of cues, with groups counted once.
export function sumDurations(cues) {
  return cues.reduce((sum, c) => sum + effectiveDurationSec(c), 0);
}

// Scheduled clock in-time for every cue, aligned index-for-index with `cues`.
// Anchored at the SHOW_START cue when one exists (times walk forward from it and
// backward for any pre-show cues); otherwise the first cue starts at show start.
// Returns an array of Date | null (null when no start time is configured).
export function computeInTimes(cues, show) {
  const showStartDate = parseShowStartDate(show);
  if (!showStartDate) return cues.map(() => null);
  const showStartIdx = cues.findIndex(c => c.cue_type === 'SHOW_START');
  if (showStartIdx === -1) {
    let ms = showStartDate.getTime();
    return cues.map(cue => {
      const t = new Date(ms);
      ms += effectiveDurationSec(cue) * 1000;
      return t;
    });
  }
  const result = new Array(cues.length);
  let ms = showStartDate.getTime();
  for (let i = showStartIdx; i < cues.length; i++) {
    result[i] = new Date(ms);
    ms += effectiveDurationSec(cues[i]) * 1000;
  }
  ms = showStartDate.getTime();
  for (let i = showStartIdx - 1; i >= 0; i--) {
    ms -= effectiveDurationSec(cues[i]) * 1000;
    result[i] = new Date(ms);
  }
  return result;
}

// The cue currently on air (or null).
export function selectOnAirCue(cues) {
  return cues.find(c => c.status === 'on_air') || null;
}

// The next pending cue for the DISPLAY "NEXT" marker — the first pending cue
// after the on-air one (array order), falling back to the first pending cue.
// NB: the transport's own take target is order-based and lives in RundownEditor;
// this is only the visual indicator.
export function selectNextCue(cues) {
  const idx = cues.findIndex(c => c.status === 'on_air');
  return (
    cues.slice(idx + 1).find(c => c.status === 'pending') ||
    cues.find(c => c.status === 'pending') ||
    null
  );
}

// Canonical display status for a cue, honouring the precedence carried over
// from the standard view (§4 of the redesign spec):
//   on-air > next > disabled > complete > skipped > divider > group > pending
// `isOnAir` / `isNext` are passed in because they depend on the whole cue list.
export function deriveCueStatus(cue, { isOnAir = false, isNext = false, isShowStart = false } = {}) {
  if (isOnAir) return 'on-air';
  const isDisabled = cue.enabled === false;
  if (isNext && !isDisabled) return 'next';
  if (isDisabled) return 'disabled';
  if (cue.status === 'complete') return 'complete';
  if (cue.status === 'skipped') return 'skipped';
  if (cue.cue_type === 'SECTION_BREAK' || cue.cue_type === 'SHOW_START' || isShowStart) return 'divider';
  if (cue.cue_type === 'GROUP') return 'group';
  return 'pending';
}
